import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import ReactTooltip from 'react-tooltip';
import omit from 'lodash.omit';

import DropdownMenu from '../../../../../../../../DropdownMenu';
import Input from '../../../../../../../../Input';
import {
  getInchMeasurements,
  getPercentMeasurements,
  getPercentPositionMeasurements,
} from '../../../../../CardDesignerCardElementFrame/helpers';
import { ORIENTATION } from '../../../../../../../../../../services/cards/constants';
import Label from '../../../../../../../../Label';
import SizingIcon from '../../../../../../../../../icons/SizingIcon';

const propTypes = {
  element: PropTypes.shape({
    cardElement: PropTypes.shape({
      frame: PropTypes.shape({
        width: PropTypes.number,
        height: PropTypes.number,
        left: PropTypes.number,
        top: PropTypes.number,
      }),
      z_index: PropTypes.number,
    }),
  }),
  cardPrintSize: PropTypes.shape({
    id: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  cardOrientation: PropTypes.oneOf(Object.values(ORIENTATION)),
  onCardElementUpdate: PropTypes.func.isRequired,
};

const CardDesignerCardElementMeasurementControls = ({
  element,
  cardPrintSize,
  cardOrientation,
  onCardElementUpdate,
}) => {
  const { frame } = element.cardElement;
  const [width, setWidth] = useState();
  const [height, setHeight] = useState();
  const [left, setLeft] = useState();
  const [top, setTop] = useState();
  const inchMeasurements = useMemo(
    () => getInchMeasurements(frame, cardPrintSize, cardOrientation),
    [frame],
  );

  useEffect(() => {
    setWidth(inchMeasurements?.width ?? 0);
    setHeight(inchMeasurements?.height ?? 0);
    setLeft(inchMeasurements?.x ?? 0);
    setTop(inchMeasurements?.y ?? 0);
  }, [frame]);

  return (
    <DropdownMenu
      placement="bottom-start"
      dropdownClassName="card-designer-card-element-common-control-dropdown card-designer-card-element-measurement-controls"
      toggleClassName="ml-1 d-flex align-items-center"
      toggle={
        <span
          data-for="measurement-controls"
          data-place="bottom"
          data-effect="solid"
          data-tip
        >
          <SizingIcon size={22} />
          <ReactTooltip id="measurement-controls" className="tooltip">
            Sizing & Position
          </ReactTooltip>
        </span>
      }
    >
      <div className="p-2 w-100 text-dark">
        <div className="d-flex align-items-center mb-2">
          <div className="measurement-div">
            <Label id="placement-left">Left&nbsp;</Label>
            <Input
              id="placement-left"
              type="number"
              max={inchMeasurements.maxX}
              min={inchMeasurements.minX ?? 0}
              step={0.01}
              value={left}
              onChange={({ target }) => {
                const inchLeft = Math.max(
                  inchMeasurements.minX ?? 0,
                  Math.min(inchMeasurements.maxX, target.value ?? 0),
                );

                const { x: percentX } = getPercentPositionMeasurements(
                  inchLeft,
                  top,
                  cardPrintSize,
                  cardOrientation,
                );
                setLeft(target.value ? inchLeft : undefined);

                if (typeof inchLeft !== 'number') {
                  return;
                }

                onCardElementUpdate({
                  cardElement: element.cardElement,
                  data: {
                    frame: {
                      ...omit(frame, '__typename'),
                      x: percentX,
                    },
                  },
                });
              }}
            />
          </div>
          <span className="mx-2">|</span>
          <div className="measurement-div">
            <Label id="placement-top">Top&nbsp;</Label>
            <Input
              id="placement-top"
              type="number"
              max={inchMeasurements.maxY}
              min={inchMeasurements.minY ?? 0}
              step={0.01}
              value={top}
              onChange={({ target }) => {
                const inchTop = Math.max(
                  inchMeasurements.minY ?? 0,
                  Math.min(inchMeasurements.maxY, target.value ?? 0),
                );

                const { y: percentY } = getPercentPositionMeasurements(
                  left,
                  inchTop,
                  cardPrintSize,
                  cardOrientation,
                );
                setTop(target.value ? inchTop : undefined);

                if (typeof inchTop !== 'number') {
                  return;
                }

                onCardElementUpdate({
                  cardElement: element.cardElement,
                  data: {
                    frame: {
                      ...omit(frame, '__typename'),
                      y: percentY,
                    },
                  },
                });
              }}
            />
          </div>
        </div>
        <div className="d-flex align-items-center">
          <div className="measurement-div">
            <Label id="measurement-width">Width&nbsp;</Label>
            <Input
              id="measurement-width"
              type="number"
              max={inchMeasurements.maxWidth}
              min={0}
              step={0.01}
              value={width}
              onChange={({ target }) => {
                const inchWidth = Math.min(
                  inchMeasurements.maxWidth,
                  target.value ?? 0,
                );

                const { width: percentWidth } = getPercentMeasurements(
                  inchWidth,
                  height,
                  cardPrintSize,
                  cardOrientation,
                );
                setWidth(target.value ? inchWidth : undefined);

                if (!inchWidth) {
                  return;
                }

                onCardElementUpdate({
                  cardElement: element.cardElement,
                  data: {
                    frame: {
                      ...omit(frame, '__typename'),
                      width: percentWidth,
                    },
                  },
                });
              }}
            />
          </div>
          <span className="mx-2">&times;</span>
          <div className="measurement-div">
            <Label id="measurement-height">Height&nbsp;</Label>
            <Input
              id="measurement-height"
              type="number"
              max={inchMeasurements.maxHeight}
              min={0}
              step={0.01}
              value={height}
              onChange={({ target }) => {
                const inchHeight = Math.min(
                  inchMeasurements.maxHeight,
                  target.value ?? 0,
                );
                const { height: percentHeight } = getPercentMeasurements(
                  width,
                  inchHeight,
                  cardPrintSize,
                  cardOrientation,
                );
                setHeight(target.value ? inchHeight : undefined);

                if (!inchHeight) {
                  return;
                }

                onCardElementUpdate({
                  cardElement: element.cardElement,
                  data: {
                    frame: {
                      ...omit(frame, '__typename'),
                      height: percentHeight,
                    },
                  },
                });
              }}
            />
          </div>
        </div>
      </div>
    </DropdownMenu>
  );
};

CardDesignerCardElementMeasurementControls.propTypes = propTypes;

export default CardDesignerCardElementMeasurementControls;
